import { useMemo } from "react";

import { AppButton } from "app/components/common";
import {
    LendingVaultExpanded,
    UserVaultExternalRewardsPnlResponse,
    useUserVaultPositionExternalRewards,
    useVaultClaimExternalRewardsTransactions
} from "@bridgesplit/abf-react";
import { useTransactionSender } from "app/components/transactions";
import { LOADING_ERROR, Result } from "@bridgesplit/utils";
import { ClaimLendVaultRewardsParams } from "@bridgesplit/abf-sdk";

interface VaultExternalRewardsClaimCtaProps {
    vaultExpanded: LendingVaultExpanded | undefined;
    claimMints?: string[];
    shortText?: boolean;
    fullWidth?: boolean;
    loading?: boolean;
    variant?: "contained" | "outlined";
}

export function VaultExternalRewardsClaimCta({
    vaultExpanded,
    claimMints,
    shortText = false,
    fullWidth,
    loading,
    variant = "contained"
}: VaultExternalRewardsClaimCtaProps) {
    const { data: userPositionPnl } = useUserVaultPositionExternalRewards(vaultExpanded);

    const claimRewards = useVaultClaimExternalRewards(vaultExpanded, claimMints, userPositionPnl);

    const pendingRewards = useMemo(() => {
        if (!userPositionPnl || !vaultExpanded) return false;

        const vaultRewards = userPositionPnl[vaultExpanded.vault.address];
        if (!vaultRewards) return false;

        return Object.values(vaultRewards).some((stakeRewards) =>
            Object.values(stakeRewards as Record<string, { pendingRewards: number }>).some(
                (reward) => reward.pendingRewards > 0
            )
        );
    }, [userPositionPnl, vaultExpanded]);

    const ctaError = useMemo(() => {
        if (!vaultExpanded) return "Loading";
        if (!pendingRewards) return "Claimed";

        return undefined;
    }, [vaultExpanded, pendingRewards]);

    return (
        <AppButton
            loading={loading}
            fullWidth={fullWidth}
            asyncCta={{ onClickWithResult: claimRewards }}
            isTransaction
            color={"primary"}
            variant={variant}
            disabled={!!ctaError}
            sx={{ minWidth: "110px" }}
        >
            {ctaError ? ctaError : shortText ? "Claim" : "Claim rewards"}
        </AppButton>
    );
}

function useVaultClaimExternalRewards(
    vaultExpanded: LendingVaultExpanded | undefined,
    claimMints?: string[],
    userPositionPnl?: UserVaultExternalRewardsPnlResponse | null
) {
    const claimRewards = useVaultClaimExternalRewardsTransactions();
    const send = useTransactionSender();

    const claimAll = claimMints === undefined;

    const userVaultSpecificRewards = Object.values(userPositionPnl?.[vaultExpanded?.vault.address ?? ""] ?? {}).flatMap(
        (stakeRewards) => Object.keys(stakeRewards)
    );

    return async () => {
        if (!vaultExpanded || !userPositionPnl) {
            return Result.errFromMessage(LOADING_ERROR);
        }

        const rewardMints = claimAll ? userVaultSpecificRewards : claimMints;

        const params: ClaimLendVaultRewardsParams = {
            vaultAddress: vaultExpanded.vault.address,
            unclaimedStakeAccounts: [],
            rewardMints
        };

        return await send(claimRewards, params);
    };
}
